import React, { useState, useEffect, useRef } from 'react';
import { colors, typography } from '../../styles/designTokens';

/**
 * ============================================
 * הסבר על הקומפוננטה הזו:
 * ============================================
 * 
 * הקומפוננטה הזו מציגה כרטיס הסבר אחד (כותרת, תיאור, פס כתום)
 * 
 * ============================================
 * איך לשנות את הגודל של הכרטיס:
 * ============================================
 * 
 * 1. גודל הכרטיס (רוחב):
 *    - מצאי את השורה: minWidth: '160px' (שורה 105)
 *    - מצאי את השורה: maxWidth: '220px' (שורה 106)
 *    - שני את הערכים (למשל: '180px', '250px')
 * 
 * 2. גודל הכותרת:
 *    - מצאי את השורה: fontSize: 'clamp(0.95rem, 1.5vw, 1.1rem)' (שורה 133)
 *    - שני את הערכים (למשל: 'clamp(1rem, 2vw, 1.3rem)' לגדול יותר)
 * 
 * 3. גודל התיאור:
 *    - מצאי את השורה: fontSize: 'clamp(0.8rem, 1.2vw, 0.9rem)' (שורה 152)
 *    - שני את הערכים (למשל: 'clamp(0.9rem, 1.5vw, 1.1rem)' לגדול יותר)
 * 
 * 4. עובי הפס הכתום:
 *    - מצאי את השורה: height: '1px' (שורה 171)
 *    - שני את הערך (למשל: '2px' לעבה יותר)
 * 
 * 5. ריווח פנימי של הכרטיס:
 *    - מצאי את השורה: padding: 'clamp(12px, 1.5vw, 16px) clamp(16px, 2vw, 20px)' (שורה 107)
 *    - שני את הערכים (למשל: '15px 20px' לריווח גדול יותר)
 */

interface AnnotationPointProps {
  annotation: {
    id: number;
    x: number;
    y: number;
    cardX: number;
    cardY: number;
    title: string;
    description: string;
    lineDirection: 'left' | 'right' | 'top' | 'bottom';
  };
  index: number;
  isVisible: boolean;
  isMoving?: boolean; // האם הכרטיס זז כרגע
  machineContainerRef?: React.RefObject<HTMLDivElement>; // ref לקונטיינר המכונה
}

const AnnotationPoint: React.FC<AnnotationPointProps> = ({ 
  annotation, 
  index, 
  isVisible,
  isMoving = false,
  machineContainerRef
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const delay = index * 200; // Staggered animation
  const cardRef = useRef<HTMLDivElement>(null);
  const [cardPosition, setCardPosition] = useState({ x: annotation.cardX, opacity: 1 });

  // אנימציה של תזוזה מימין לשמאל כשהכרטיס זז
  useEffect(() => {
    if (!isMoving || !cardRef.current || !machineContainerRef?.current) {
      // אם הכרטיס לא זז - חזרה למיקום המקורי
      setCardPosition({ x: annotation.cardX, opacity: 1 });
      return;
    }

    // מיקום התחלתי - מימין
    const startX = annotation.cardX;
    // מיקום סיום - שמאלה (לכיוון המכונה) - עד לחלק השחור
    // נניח שהחלק השחור מתחיל ב-30% מהרוחב (אפשר לשנות)
    const endX = 30; // מיקום החלק השחור
    const bladeImageX = 50; // מיקום תמונת הלהבים (בערך במרכז)

    // זמן התזוזה - 1.5 שניות
    const duration = 1500;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // תזוזה חלקה מימין לשמאל
      const currentX = startX - (startX - endX) * progress;
      
      // העלמות כשעוברים מעל תמונת הלהבים (בערך ב-50% מהדרך)
      let opacity = 1;
      if (currentX <= bladeImageX) {
        // כשעוברים מעל תמונת הלהבים - מתחילים להיעלם
        const fadeProgress = (bladeImageX - currentX) / (bladeImageX - endX);
        opacity = Math.max(0, 1 - fadeProgress * 2); // נעלם מהר יותר
      }

      setCardPosition({ x: currentX, opacity });

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // סיום התזוזה - הכרטיס נעלם
        setCardPosition({ x: endX, opacity: 0 });
      }
    };

    requestAnimationFrame(animate);
  }, [isMoving, annotation.cardX, machineContainerRef]);

  // הקו מתחיל מהעיגול על המכונה ומגיע עד לכרטיס
  // אם הכרטיס מימין: פס אופקי ארוך
  // אם הכרטיס משמאל: פס שבור - אופקי ואז אנכי לגובה של מתחת לכותרת (כמו ב-"2 מנועים")
  const isCardOnRight = annotation.cardX > 50;
  
  // נקודת ההתחלה - מהעיגול על המכונה
  const lineStart = { 
    x: annotation.x, // x של המכונה (איפה העיגול)
    y: annotation.y  // y של המכונה (איפה העיגול)
  };
  
  // נקודת הסיום - עד לכרטיס
  // הקו צריך להיות אופקי ארוך מהנקודה עד לכרטיס (כמו ב-"2 מנועים")
  // אם זה "2 מנועים" (id: 3), נקצר את הפס קצת
  const isMotors = annotation.id === 3;
  const cardEdgeOffset = isCardOnRight 
    ? (isMotors ? -12 : -5) // לכרטיס מימין - אם זה "2 מנועים" קצר יותר (מ--10 ל--12)
    : 5; // לכרטיס משמאל
  const lineEnd = {
    x: annotation.cardX + cardEdgeOffset, // עד לפני המלל (לא נוגע במלל) - קצר יותר עבור "2 מנועים"
    y: annotation.y // אותו גובה כמו הנקודה - פס אופקי ארוך
  };

  return (
    <>
      {/* Pointer Line - קו מנחה לבן ישר - מוצג */}
      {/* ה-SVG ממוקם בקונטיינר של המכונה (machine-image-wrapper) */}
      {/* חשוב: ה-SVG צריך להיות מחוץ לכרטיס, בקונטיינר של המכונה */}
      <svg
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 5, // מעל המכונה, מתחת לכרטיסים
          opacity: isVisible ? 1 : 0,
          transition: `opacity 0.8s ease-out ${delay + 300}ms`,
          overflow: 'visible', // כדי שהעיגול לא ייחתך כשהוא זז
        }}
      >
        {/* פס ישר אופקי ארוך: מהעיגול על המכונה עד לכרטיס (כמו ב-"2 מנועים") */}
        <line
          x1={`${lineStart.x}%`}
          y1={`${lineStart.y}%`}
          x2={`${lineEnd.x}%`}
          y2={`${lineEnd.y}%`}
          stroke="#FFFFFF" // לבן תמיד
          strokeWidth="1px" // עובי דק - כמו הפס הכתום
          strokeLinecap="round"
          style={{
            transition: 'all 0.3s ease-out',
            filter: `drop-shadow(0 0 2px rgba(255, 255, 255, 0.8)) drop-shadow(0 0 4px rgba(255, 255, 255, 0.6))`, // זוהר לבן עדין
            opacity: 1,
          }}
        />
        {/* נקודה מאירה בקצה הקו על המכונה - לבן עם אור צהבהב */}
        <circle
          cx={`${lineStart.x}%`}
          cy={`${lineStart.y}%`}
          r={isVisible ? (isHovered ? '5px' : '4px') : '0px'} // גדל מעט ב-hover
          fill="#FFFFFF" // לבן
          className="annotation-circle-pulse"
          style={{
            transition: `transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay + 300}ms`,
            filter: `drop-shadow(0 0 5px rgba(255, 255, 255, 1)) drop-shadow(0 0 10px rgba(255, 215, 0, 0.9)) drop-shadow(0 0 15px rgba(255, 215, 0, 0.7)) drop-shadow(0 0 20px rgba(255, 215, 0, 0.5))`, // לבן עם זוהר צהבהב
            transform: isHovered 
              ? 'scale(1.05)' // גדילה קטנה ב-hover
              : undefined, // האנימציה תטפל ב-transform
            animation: isVisible ? 'annotationCirclePulse 2.5s ease-in-out infinite' : 'none',
          }}
        />
      </svg>

      {/* 
        ============================================
        כרטיס הטקסט - הכרטיס שמציג את ההסבר:
        ============================================
        זה הכרטיס שמציג את הכותרת, התיאור והפס הכתום
      */}
      <div
        ref={cardRef}
        style={{
          // מיקום הכרטיס על המסך
          position: 'absolute', // מיקום אבסולוטי - אפשר לשים אותו בכל מקום
          left: `${cardPosition.x}%`, // מיקום אופקי - משתנה כשהכרטיס זז
          top: `${annotation.cardY}%`, // מיקום אנכי - נקבע לפי cardY (0% = למעלה, 100% = למטה)
          
          // ============================================
          // גודל הכרטיס - כאן משנים את הגודל!
          // ============================================
          minWidth: 'clamp(140px, 160px, 160px)', // מובייל: 140px, דסקטופ: 160px
          maxWidth: 'clamp(180px, 220px, 220px)', // מובייל: 180px, דסקטופ: 220px
          
          // ריווח פנימי של הכרטיס (מרווח בין הטקסט לקצה הכרטיס)
          padding: 'clamp(10px, 1.5vw, 16px) clamp(12px, 2vw, 20px)', // מובייל: 10px 12px, דסקטופ: 16px 20px
          // פירוש: 
          // - למעלה/למטה: מינימום 12px, מקסימום 16px
          // - משמאל/מימין: מינימום 16px, מקסימום 20px
          // אפשר לשנות ל: '10px 15px' (פשוט יותר) או '20px 25px' (יותר ריווח)
          
          // עיצוב הכרטיס
          background: 'transparent', // רקע שקוף (לא רואים רקע)
          backdropFilter: 'none', // ללא אפקט טשטוש
          borderRadius: '0px', // ללא פינות מעוגלות (פינות חדות)
          border: 'none', // ללא מסגרת
          boxShadow: 'none', // ללא צל
          
          // אנימציה והצגה
          // שקיפות - משלבת את isVisible עם cardPosition.opacity (להעלמות בתזוזה)
          opacity: isVisible ? cardPosition.opacity : 0,
          transform: isVisible
            ? isHovered
              ? 'translate(-50%, -50%) scale(1.03)' // כשעוברים עם העכבר - גדל קצת (103%)
              : 'translate(-50%, -50%) scale(1)' // גודל רגיל (100%)
            : 'translate(-50%, -50%) scale(0.95)', // לפני הכניסה - קטן יותר (95%)
          // translate(-50%, -50%) = ממרכז את הכרטיס על המיקום (cardX, cardY)
          
          transition: isMoving 
            ? 'left 0.1s linear, opacity 0.3s ease-out' // תזוזה חלקה כשזז
            : `all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) ${delay + 400}ms`, // אנימציה חלקה כשעומד
          // עומק - הכרטיס תמיד מעל התמונות, במיוחד כשזז
          zIndex: isMoving ? 100 : (isHovered ? 50 : 30), // zIndex גבוה מאוד כדי להיות מעל התמונות
          cursor: 'pointer', // סמן עכבר - יד (כמו על קישור)
          
          // סידור התוכן בתוך הכרטיס
          display: 'flex', // הצגה כשורה/עמודה
          flexDirection: 'column', // סידור אנכי (כותרת למעלה, תיאור למטה)
          alignItems: 'flex-start', // יישור לשמאל (RTL)
          justifyContent: 'center', // יישור אנכי למרכז
        }}
        onMouseEnter={() => setIsHovered(true)} // כשעוברים עם העכבר - הגדל
        onMouseLeave={() => setIsHovered(false)} // כשעוזבים - חזור לגודל רגיל
      >
        {/* 
          ============================================
          כותרת הכרטיס (למשל: "להב מקצועי"):
          ============================================
        */}
        <h3
          style={{
            // גודל הטקסט - כאן משנים את גודל הכותרת!
            fontSize: 'clamp(0.85rem, 1.5vw, 1.1rem)', // מובייל: 13.6px, דסקטופ: 17.6px
            
            fontFamily: typography.heading.fontFamily, // גופן הכותרת
            fontWeight: 600, // עובי הטקסט (400 = רגיל, 600 = בינוני, 700 = עבה)
            color: '#FFFFFF', // צבע לבן
            marginBottom: '8px', // ריווח מתחת לכותרת (8 פיקסלים)
            lineHeight: 1.3, // גובה שורה (1.3 = 30% יותר מהגודל)
            textAlign: 'right', // יישור לימין (RTL)
            width: '100%', // רוחב מלא
            whiteSpace: 'nowrap', // הכותרת תישאר בשורה אחת
            textShadow: isHovered 
              ? `0 0 8px rgba(255, 255, 255, 0.8), 0 0 4px rgba(255, 255, 255, 0.5)` // צל חזק יותר ב-hover
              : '0 0 4px rgba(255, 255, 255, 0.3)', // צל עדין
            transition: 'all 0.3s ease-out', // אנימציה חלקה
          }}
        >
          {annotation.title}
        </h3>
        
        {/* 
          ============================================
          תיאור הכרטיס (למשל: "ביצור מיוחד לשימוש יעיל ונוח"):
          ============================================
        */}
        <p
          style={{
            // גודל הטקסט - כאן משנים את גודל התיאור!
            fontSize: 'clamp(0.75rem, 1.2vw, 0.9rem)', // מובייל: 12px, דסקטופ: 14.4px
            
            fontFamily: typography.body.fontFamily, // גופן הטקסט
            color: '#FFFFFF', // צבע לבן
            lineHeight: 1.5, // גובה שורה (1.5 = 50% יותר מהגודל)
            textAlign: 'right', // יישור לימין (RTL)
            margin: 0, // ללא ריווח חיצוני
            marginBottom: '12px', // ריווח מתחת לתיאור לפני הפס הכתום
            width: '100%', // רוחב מלא
            whiteSpace: 'pre-line', // שומר על שורות (תמיכה ב-\n)
            opacity: isHovered ? 1 : 0.85, // שקיפות - 1 = נראה, 0.85 = קצת שקוף
            textShadow: '0 0 3px rgba(255, 255, 255, 0.2)', // צל עדין
            transition: 'opacity 0.3s ease-out', // אנימציה חלקה
          }}
        >
          {annotation.description}
        </p>
        {/* 
          ============================================
          פס כתום קצר מתחת לכל המלל (כותרת + תיאור):
          ============================================
          זה הפס הכתום הקצר שמוצג מתחת לכל הטקסט בלבד
        */}
        <div style={{ position: 'relative', width: '100%', marginTop: '0px' }}>
          {/* פס קצר - רק מתחת למלל */}
          <div
            style={{
              width: '100%', // רוחב מלא (100% מהכרטיס)
              height: '2px', // עובי של 2 פיקסל
              background: isHovered 
                ? colors.gold.light
                : colors.gold.mediumLight,
              boxShadow: isHovered 
                ? `0 0 4px ${colors.gold.light}`
                : `0 0 2px ${colors.gold.mediumLight}`,
              transition: 'all 0.3s ease-out',
            }}
          />
        </div>
      </div>
    </>
  );
};

export default AnnotationPoint;
